// src/pages/Terms.tsx
import React from "react";
import { Link } from "react-router-dom";
import {
  Scale,
  Shield,
  AlertCircle,
  BookOpen,
  CheckCircle,
  Sparkles,
  Key,
  Globe,
  Zap,
  FileText,
  Target,
  Lock,
  ShieldCheck,
} from "lucide-react";
import logo from "../assets/images/logo.png";
import PublicNavbar from "../components/PublicNavbar";
import { GlobalStyles } from "../styles/globalStyles";

const Terms: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-950">
      {/* Navigation */}
      <PublicNavbar currentPage="/terms" />

      {/* Header */}
      <section className="relative overflow-hidden pt-24 pb-20 lg:pt-28 lg:pb-24">
        {/* Animated Background Elements */}
        <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-primary/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl"></div>

        <div className="container mx-auto px-4 lg:px-5 py-20 relative">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 text-primary text-sm font-medium mb-6 backdrop-blur-sm">
              <Scale className="h-4 w-4 mr-2" />
              Legal Terms & Conditions
            </div>
            <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 dark:text-white mb-6">
              Terms of Service
            </h1>
            <p className="text-lg lg:text-xl text-gray-600 dark:text-gray-400 mb-8 max-w-3xl mx-auto">
              Last updated:{" "}
              {new Date().toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <div className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm">
                Legal Agreement
              </div>
              <div className="px-3 py-1 bg-blue-500/10 text-blue-500 rounded-full text-sm">
                Service Terms
              </div>
              <div className="px-3 py-1 bg-emerald-500/10 text-emerald-500 rounded-full text-sm">
                User Responsibilities
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 lg:py-20">
        <div className="container mx-auto px-4 lg:px-5">
          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {/* Quick Navigation */}
              <div className={`${GlobalStyles.card} mb-12`}>
                <div className="flex items-center gap-3 mb-4">
                  <BookOpen className="h-5 w-5 text-primary" />
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                    Quick Navigation
                  </h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { label: "1. Agreement", section: "#agreement" },
                    { label: "2. Services", section: "#services" },
                    {
                      label: "3. Responsibilities",
                      section: "#responsibilities",
                    },
                    { label: "4. Fees", section: "#fees" },
                    { label: "5. Liability", section: "#liability" },
                    { label: "6. IP Rights", section: "#ip" },
                    { label: "7. Termination", section: "#termination" },
                    { label: "8. Contact", section: "#contact" },
                  ].map((item, idx) => (
                    <a
                      key={idx}
                      href={item.section}
                      className="px-3 py-2 text-sm bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-primary/10 hover:text-primary transition-colors text-center"
                    >
                      {item.label}
                    </a>
                  ))}
                </div>
              </div>

              {/* 1. Agreement to Terms */}
              <div className={`${GlobalStyles.card}`} id="agreement">
                <div className="flex items-center gap-3 mb-6">
                  <FileText className="h-6 w-6 text-primary" />
                  <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
                    1. Agreement to Terms
                  </h2>
                </div>
                <div className="space-y-4 text-gray-600 dark:text-gray-400">
                  <p>
                    Welcome to Pelivor Recovery. These Terms and Conditions
                    constitute a legally binding agreement made between you,
                    whether personally or on behalf of an entity ("you") and
                    Pelivor Recovery ("we", "us", or "our"), concerning your
                    access to and use of our recovery services.
                  </p>
                  <p>
                    By accessing or using our website, recovery portal, or any
                    other services (collectively, the "Services"), you
                    acknowledge that you have read, understood, and agree to be
                    bound by these Terms. If you do not agree with all of these
                    Terms, you are prohibited from using the Services and must
                    discontinue use immediately.
                  </p>
                  <div className="bg-primary/5 rounded-xl p-4 border border-primary/20">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      <span className="font-semibold">Important:</span> By using
                      our services, you confirm that you are at least 18 years
                      old and have the legal capacity to enter into this
                      agreement.
                    </p>
                  </div>
                </div>
              </div>

              {/* 2. Services Description */}
              <div className={`${GlobalStyles.card}`} id="services">
                <div className="flex items-center gap-3 mb-6">
                  <ShieldCheck className="h-6 w-6 text-primary" />
                  <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
                    2. Services Description
                  </h2>
                </div>
                <div className="space-y-6">
                  <p className="text-gray-600 dark:text-gray-400">
                    Pelivor Recovery provides professional cryptocurrency
                    recovery services including:
                  </p>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl p-5 border border-primary/20 group hover:scale-[1.02] transition-all duration-300">
                      <div className="flex items-center gap-3 mb-3">
                        <Target className="h-5 w-5 text-primary" />
                        <h3 className="font-bold text-gray-900 dark:text-white">
                          Blockchain Forensic Analysis
                        </h3>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Expert tracing and analysis of blockchain transactions
                        to identify recovery opportunities across multiple
                        networks.
                      </p>
                    </div>
                    <div className="bg-gradient-to-br from-blue-500/5 to-blue-500/10 rounded-xl p-5 border border-blue-500/20 group hover:scale-[1.02] transition-all duration-300">
                      <div className="flex items-center gap-3 mb-3">
                        <Zap className="h-5 w-5 text-blue-500" />
                        <h3 className="font-bold text-gray-900 dark:text-white">
                          Asset Recovery Services
                        </h3>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Professional recovery of lost, stolen, or inaccessible
                        cryptocurrency assets through investigation and
                        negotiation.
                      </p>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-amber-500/5 to-amber-500/10 rounded-xl p-5 border border-amber-500/20">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white mb-1">
                          Important Security Notice
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Pelivor Recovery does NOT store private keys, seed
                          phrases, or provide wallet services. We assist with
                          recovery through forensic analysis and legal channels
                          only.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 3. User Responsibilities */}
              <div className={`${GlobalStyles.card}`} id="responsibilities">
                <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-6">
                  3. User Responsibilities
                </h2>
                <div className="space-y-6">
                  <p className="text-gray-600 dark:text-gray-400">
                    By using our Services, you agree to comply with the
                    following responsibilities:
                  </p>

                  <div className="bg-gradient-to-br from-emerald-500/5 to-emerald-500/10 rounded-xl p-6 border border-emerald-500/20">
                    <div className="space-y-4">
                      {[
                        "Provide accurate and complete information about lost assets and circumstances",
                        "Maintain the security of your account credentials and authentication methods",
                        "Cooperate fully with our recovery team during the investigation process",
                        "Notify us immediately of any relevant updates or new information",
                        "Comply with all applicable laws and regulations in your jurisdiction",
                        "Pay all agreed-upon fees promptly upon successful recovery",
                      ].map((item, idx) => (
                        <div key={idx} className="flex items-start">
                          <CheckCircle className="h-5 w-5 text-emerald-500 mr-3 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700 dark:text-gray-300">
                            {item}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* 4. Fees & Payment */}
              <div className={`${GlobalStyles.card}`} id="fees">
                <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-6">
                  4. Fees & Payment Terms
                </h2>
                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl p-5 border border-primary/20">
                      <h4 className="font-bold text-gray-900 dark:text-white mb-3">
                        Recovery Service Fees
                      </h4>
                      <div className="space-y-2">
                        <p className="text-sm">
                          Success-based fee:{" "}
                          <span className="font-semibold">
                            20-30% of recovered amount
                          </span>
                        </p>
                        <p className="text-sm">
                          Minimum fee:{" "}
                          <span className="font-semibold">$500</span>
                        </p>
                        <p className="text-sm">
                          Consultation fee:{" "}
                          <span className="font-semibold">$99</span> (refundable
                          if we proceed)
                        </p>
                        <p className="text-xs text-gray-500">
                          All fees due only upon successful recovery
                        </p>
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-blue-500/5 to-blue-500/10 rounded-xl p-5 border border-blue-500/20">
                      <h4 className="font-bold text-gray-900 dark:text-white mb-3">
                        Payment Terms
                      </h4>
                      <div className="space-y-2">
                        <p className="text-sm">
                          Invoice issued upon successful recovery
                        </p>
                        <p className="text-sm">
                          Payment due within 7 days of invoice
                        </p>
                        <p className="text-sm">
                          Cryptocurrency payments accepted
                        </p>
                        <p className="text-xs text-gray-500">
                          No recovery, no fee policy
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-blue-500/5 to-blue-500/10 rounded-xl p-5 border border-blue-500/20">
                    <div className="flex items-start gap-3">
                      <Lock className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white mb-1">
                          Refund Policy
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Consultation fees are refundable if we determine we
                          cannot proceed with recovery. Recovery service fees
                          are only charged upon successful recovery of assets.
                          All fees are exclusive of applicable taxes and
                          transaction costs.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 5. Disclaimers & Limitations */}
              <div className={`${GlobalStyles.card}`} id="liability">
                <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-6">
                  5. Disclaimers & Limitations of Liability
                </h2>
                <div className="space-y-6">
                  <div className="bg-gradient-to-br from-rose-500/5 to-rose-500/10 rounded-xl p-5 border border-rose-500/20">
                    <h4 className="font-bold text-gray-900 dark:text-white mb-3">
                      Service Disclaimers
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      OUR SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE"
                      WITHOUT WARRANTIES OF ANY KIND. WE DO NOT GUARANTEE:
                    </p>
                    <div className="space-y-2">
                      {[
                        "100% success rate in recovering lost funds",
                        "Specific timeframe for recovery completion",
                        "Recovery of all lost funds",
                        "Protection against all future security threats",
                      ].map((item, idx) => (
                        <div key={idx} className="flex items-start text-sm">
                          <div className="w-2 h-2 bg-rose-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                          <span className="text-gray-700 dark:text-gray-300">
                            {item}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-amber-500/5 to-amber-500/10 rounded-xl p-5 border border-amber-500/20">
                    <h4 className="font-bold text-gray-900 dark:text-white mb-3">
                      Limitation of Liability
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      TO THE MAXIMUM EXTENT PERMITTED BY LAW, PELIVOR RECOVERY
                      SHALL NOT BE LIABLE FOR:
                    </p>
                    <div className="space-y-2">
                      {[
                        "Indirect, incidental, special, consequential, or punitive damages",
                        "Loss of profits, data, use, goodwill, or other intangible losses",
                        "Circumstances beyond our reasonable control",
                        "Actions or inactions of third parties including exchanges or wallet providers",
                        "Market fluctuations or changes in asset value during recovery process",
                      ].map((item, idx) => (
                        <div key={idx} className="flex items-start text-sm">
                          <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                          <span className="text-gray-700 dark:text-gray-300">
                            {item}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl p-5 border border-primary/20">
                    <div className="flex items-start gap-3">
                      <Shield className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white mb-1">
                          Professional Standards
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          While we maintain the highest professional standards
                          and use best practices, cryptocurrency recovery
                          involves inherent uncertainties. Success depends on
                          multiple factors including the specific circumstances,
                          cooperation from third parties, and technical
                          feasibility.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 6. Intellectual Property */}
              <div className={`${GlobalStyles.card}`} id="ip">
                <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-6">
                  6. Intellectual Property Rights
                </h2>
                <div className="space-y-4 text-gray-600 dark:text-gray-400">
                  <p>
                    The Services and their original content, features,
                    functionality, recovery methodologies, and technology are
                    and will remain the exclusive property of Pelivor Recovery
                    and its licensors. Our trademarks, service marks, and trade
                    dress may not be used in connection with any product or
                    service without prior written consent.
                  </p>
                  <p>
                    You are granted a limited, non-exclusive, non-transferable,
                    and revocable license to access and use the Services for
                    recovery purposes as outlined in these Terms.
                  </p>
                  <div className="bg-primary/5 rounded-xl p-4 border border-primary/20">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      <span className="font-semibold">Note:</span> Reports,
                      analysis documents, and recovery strategies provided to
                      you remain your property for personal use but may not be
                      resold, distributed, or used commercially without our
                      permission.
                    </p>
                  </div>
                </div>
              </div>

              {/* 7. Termination */}
              <div className={`${GlobalStyles.card}`} id="termination">
                <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-6">
                  7. Termination
                </h2>
                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-gradient-to-br from-blue-500/5 to-blue-500/10 rounded-xl p-5 border border-blue-500/20">
                      <h4 className="font-bold text-gray-900 dark:text-white mb-3">
                        User Termination
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        You may terminate your use of our Services at any time
                        by notifying us in writing. Termination does not affect
                        obligations incurred prior to termination.
                      </p>
                    </div>
                    <div className="bg-gradient-to-br from-rose-500/5 to-rose-500/10 rounded-xl p-5 border border-rose-500/20">
                      <h4 className="font-bold text-gray-900 dark:text-white mb-3">
                        Company Termination
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        We may terminate or suspend your access immediately for
                        breach of Terms, fraudulent activity, or failure to
                        cooperate with recovery procedures.
                      </p>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-emerald-500/5 to-emerald-500/10 rounded-xl p-5 border border-emerald-500/20">
                    <div className="flex items-start gap-3">
                      <Key className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white mb-1">
                          Post-Termination
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Upon termination, your right to use the Services
                          immediately ceases. Provisions regarding fees,
                          liability limitations, intellectual property, and
                          governing law survive termination.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 8. Governing Law */}
              <div className={`${GlobalStyles.card}`}>
                <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-6">
                  8. Governing Law & Dispute Resolution
                </h2>
                <div className="space-y-4 text-gray-600 dark:text-gray-400">
                  <p>
                    These Terms shall be governed and construed in accordance
                    with the laws of the State of Delaware, United States,
                    without regard to its conflict of law provisions.
                  </p>
                  <p>
                    Any dispute arising from or relating to these Terms or our
                    Services shall be resolved through binding arbitration in
                    Wilmington, Delaware, in accordance with the rules of the
                    American Arbitration Association.
                  </p>
                  <div className="bg-primary/5 rounded-xl p-4 border border-primary/20">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      <span className="font-semibold">
                        Arbitration Agreement:
                      </span>{" "}
                      By using our Services, you agree to resolve any disputes
                      through binding arbitration rather than in court, except
                      for matters that may be taken to small claims court.
                    </p>
                  </div>
                </div>
              </div>

              {/* 9. Changes to Terms */}
              <div className={`${GlobalStyles.card}`}>
                <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-6">
                  9. Changes to These Terms
                </h2>
                <div className="space-y-4 text-gray-600 dark:text-gray-400">
                  <p>
                    We reserve the right to modify or replace these Terms at any
                    time. If a revision is material, we will provide at least 30
                    days' notice prior to any new terms taking effect. What
                    constitutes a material change will be determined at our sole
                    discretion.
                  </p>
                  <p>
                    By continuing to access or use our Services after revisions
                    become effective, you agree to be bound by the revised
                    terms. If you do not agree to the new terms, you must stop
                    using the Services.
                  </p>
                </div>
              </div>

              {/* 10. Contact Information */}
              <div className={`${GlobalStyles.card}`} id="contact">
                <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-6">
                  10. Contact Information
                </h2>
                <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl p-8 border border-primary/20">
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    If you have any questions about these Terms or need legal
                    assistance, please contact our Legal Department:
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <Globe className="h-5 w-5 text-primary" />
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          Website
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          pelivorrecovery.org
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Sparkles className="h-5 w-5 text-primary" />
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          Email
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          legal@pelivorrecovery.org
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Scale className="h-5 w-5 text-primary" />
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          Mailing Address
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          123 Legal Avenue, Wilmington, Delaware 19801, USA
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="container mx-auto px-4 lg:px-5">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img src={logo} alt="Pelivor Recovery" className="h-8 w-auto" />
                <span className="text-xl font-bold text-white">
                  Pelivor Recovery
                </span>
              </div>
              <p className="mb-4 text-sm">
                Professional cryptocurrency recovery services with expertise in
                blockchain forensic analysis and asset recovery.
              </p>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Services</h4>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/recovery"
                    className="hover:text-white transition-colors"
                  >
                    Asset Recovery
                  </Link>
                </li>
                <li>
                  <Link
                    to="/forensics"
                    className="hover:text-white transition-colors"
                  >
                    Forensic Analysis
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Legal</h4>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/terms"
                    className="hover:text-white transition-colors"
                  >
                    Terms & Conditions
                  </Link>
                </li>
                <li>
                  <Link
                    to="/privacy"
                    className="hover:text-white transition-colors"
                  >
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Contact</h4>
              <p className="mb-2 text-sm">support@pelivorrecovery.org</p>
              <p className="text-sm">24/7 Recovery Support</p>
              <p className="text-sm mt-2">pelivorrecovery.org</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-sm">
            <p>
              © {new Date().getFullYear()} Pelivor Recovery. All rights
              reserved. Professional cryptocurrency recovery services.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Terms;
